import { Box } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LogoAnimationLoader } from 'src/components/LogoLoader';
import ScrollToTop from 'src/components/ScrollToTop';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { ErrorOutput } from 'src/types';
import { VALIDATE_POLICY_QUERY, ValidatePolicyResponse } from '../../configs/APISchemas/ValidatePolicyGraphQL';
import { useMount } from '../../helpers/hooks/useMount';
import { updateVerifyPolicyFlow } from '../../redux/actions/verifyPolicy';
import { getUtmSource } from '../../redux/selectors/system';
import { getVerifyPolicyInput } from '../../redux/selectors/verifyPolicy';
import CoverageErrors from '../../components/CoverageErrors';
import CoverageCompliant from '../../components/CoverageCompliant';
import NoPolicyLocated from '../../components/NoPolicyLocated';
import { ApplicationStatus } from './Compliance.types';
import RentersInsuranceLayout from '../../layouts/RentersInsuranceLayout';
import { UploadSuccess } from 'src/components/UploadSuccess';

const Compliance = () => {
  const [status, setStatus] = useState<ApplicationStatus>(
    ApplicationStatus.INPROGRESS,
  );
  const [errors, setErrors] = useState<ErrorOutput[]>([]);
  const params: any = useParams();
  const verifyPolicyInput = useSelector(getVerifyPolicyInput);
  const utm_source = useSelector(getUtmSource);
  const dispatch = useDispatch();

  useMount(() => {
    (async () => {
      try {
        const mutation = VALIDATE_POLICY_QUERY(verifyPolicyInput, {
          policy_id: params.covie_id,
          utm_source,
        });
        dispatch(
          updateVerifyPolicyFlow({
            userFinishedComplianceFlow: true,
          }),
        );
        const response = await GraphQLRequest<ValidatePolicyResponse>(mutation);
        if (response.validateCoviePolicy) {
          if ('errors' in response.validateCoviePolicy) {
            if (
              response.validateCoviePolicy.errors[0].type ===
              'APPLICATION_ALREADY_COMPLIANT'
            ) {
              setStatus(ApplicationStatus.COMPLIANT);
              return;
            }
            if (response.validateCoviePolicy.errors[0].type === 'Error') {
              setStatus(ApplicationStatus.ERROR);
              return;
            } else if (
              response.validateCoviePolicy.errors.some(
                (error: { type: string }) =>
                  [
                    'PERSONAL_LIABILITY',
                    'PERSONAL_PROPERTY',
                    'MEDICAL_PAYMENTS',
                  ].includes(error.type),
              )
            ) {
              setErrors(response.validateCoviePolicy.errors);
              setStatus(ApplicationStatus.NOTCOMPLIANT);
            } else if (
              response.validateCoviePolicy.errors.some(
                (error: { type: string }) =>
                  [
                    'ACTIVE_POLICY_NOT_FOUND',
                    'POLICY_DATES_INVALID',
                    'NAME_DOES_NOT_MATCH',
                    'ADDRESS_DOES_NOT_MATCH',
                  ].includes(error.type),
              )
            ) {
              setStatus(ApplicationStatus.NEEDSREVIEW);
            } else {
              setStatus(ApplicationStatus.ERROR);
            }
            return;
          } else if (response.validateCoviePolicy.validation?.errors) {
            if (
              response.validateCoviePolicy.validation?.errors[0].type ===
              'Error'
            ) {
              setStatus(ApplicationStatus.ERROR);
              return;
            } else if (
              response.validateCoviePolicy.validation?.errors.some(
                (error: { type: string }) =>
                  [
                    'PERSONAL_LIABILITY',
                    'PERSONAL_PROPERTY',
                    'MEDICAL_PAYMENTS',
                  ].includes(error.type),
              )
            ) {
              setErrors(response.validateCoviePolicy.validation?.errors);
              setStatus(ApplicationStatus.NOTCOMPLIANT);
              return;
            }
            setStatus(ApplicationStatus.NEEDSREVIEW);
            return;
          } else if (response.validateCoviePolicy.application?.id) {
            setStatus(response.validateCoviePolicy.application.status);
          }
          //success here
          return;
        }
        setStatus(ApplicationStatus.ERROR);
        return;
      } catch (error: any) {
        if (error.response?.data?.errors) {
          setStatus(ApplicationStatus.NEEDSREVIEW);
        } else {
          setStatus(ApplicationStatus.ERROR);
        }
      }
    })();
  });
  //TODO add status in-progress for loading results

  if (status === ApplicationStatus.COMPLIANT) {
    return (
      <RentersInsuranceLayout>
        <CoverageCompliant/>
      </RentersInsuranceLayout>
    );
  }

  if (status === ApplicationStatus.NEEDSREVIEW) {
    return (
      <RentersInsuranceLayout>
        <UploadSuccess/>
      </RentersInsuranceLayout>
    );
  }
  if (status === ApplicationStatus.NOTCOMPLIANT) {
    return (
      <RentersInsuranceLayout>
        <CoverageErrors applicationErrors={errors}/>
      </RentersInsuranceLayout>
    );
  }

  if (status === ApplicationStatus.ERROR) {
    return (
      <RentersInsuranceLayout>
        <NoPolicyLocated/>
      </RentersInsuranceLayout>
    );
  }

  return (
    <RentersInsuranceLayout withFooter={false}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <ScrollToTop/>
        <LogoAnimationLoader/>
      </Box>
    </RentersInsuranceLayout>
  );
};
export default Compliance;
